import React, { useState, useEffect } from 'react';
import OwlTalkBox from '../components/OwlTalkBox';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import globalLeaderboardData from '../data/global-leaderboard.json';
import friendsLeaderboardData from '../data/friends-leaderboard.json';
import { LeaderboardSkeleton } from '../components/SkeletonLoader';

const Leaderboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('global');
  const [loading, setLoading] = useState(true);

  // Read leaderboard data from JSON files
  const globalLeaderboard = globalLeaderboardData;
  const friendsLeaderboard = friendsLeaderboardData;

  // Simulate loading time
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500); // 1.5 seconds loading time

    return () => clearTimeout(timer);
  }, []);

  const currentUserData = {
    name: user?.full_name || user?.email?.split('@')[0] || 'You',
    coins: 25,
    streak: 2,
    avatar: "👤"
  };

  const renderLeaderboardTable = (data, showCurrentUser = false) => (
    <motion.div
      className="bg-gradient-to-r from-yellow-100 to-blue-100 dark:from-blue-900 dark:to-yellow-700 rounded-2xl shadow p-6 w-full"
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      whileHover={{ scale: 1.02, boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)' }}
      whileTap={{ scale: 0.98 }}
    >
      <table className="w-full text-left rounded-2xl overflow-hidden">
        <thead>
          <tr>
            <th className="font-inter py-2 text-lg text-pink-700 dark:text-yellow-200">Rank</th>
            <th className="font-inter py-2 text-lg text-pink-700 dark:text-yellow-200">Name</th>
            <th className="font-inter py-2 text-lg text-yellow-600 dark:text-yellow-300">Coins</th>
            <th className="font-inter py-2 text-lg text-blue-700 dark:text-blue-200">Streak</th>
          </tr>
        </thead>
        <tbody>
          {data.map((user, i) => (
            <tr 
              key={user.name} 
              className={`${
                i === 0 ? 'font-bold text-yellow-600 dark:text-yellow-300 bg-yellow-200 dark:bg-blue-900' : 
                i === 1 ? 'font-semibold text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-800' :
                i === 2 ? 'font-semibold text-orange-600 dark:text-orange-300 bg-orange-200 dark:bg-orange-900' :
                'bg-white/60 dark:bg-gray-700'
              } ${user.name === currentUserData.name ? 'ring-2 ring-purple-500 dark:ring-purple-400' : ''}`}
            >
              <td className="font-inter py-2 px-4 rounded-l-2xl">
                <div className="flex items-center gap-2">
                  {i === 0 && <span className="text-2xl">🥇</span>}
                  {i === 1 && <span className="text-2xl">🥈</span>}
                  {i === 2 && <span className="text-2xl">🥉</span>}
                  {i > 2 && <span className="text-lg font-semibold">#{i + 1}</span>}
                </div>
              </td>
              <td className="font-inter py-2 px-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{user.avatar}</span>
                  <span>{user.name}</span>
                  {user.name === currentUserData.name && (
                    <span className="text-xs bg-purple-500 text-white px-2 py-1 rounded-full">You</span>
                  )}
                </div>
              </td>
              <td className="font-inter py-2 px-4">{user.coins}</td>
              <td className="font-inter py-2 px-4 rounded-r-2xl">{user.streak}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  );

  // Show skeleton while loading
  if (loading) {
    return <LeaderboardSkeleton />;
  }

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col items-center bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-8">
      <h2 className="font-baloo text-3xl font-extrabold mb-4 text-blue-700 dark:text-blue-200">Leaderboard</h2>
      <OwlTalkBox text="Top contributors and learners!" />
      
      {/* Tab Navigation */}
      <div className="flex space-x-2 mb-6 w-full max-w-md">
        <button
          onClick={() => setActiveTab('global')}
          className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
            activeTab === 'global'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          🌍 Global
        </button>
        <button
          onClick={() => setActiveTab('friends')}
          className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
            activeTab === 'friends'
              ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-lg'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          👥 Friends
        </button>
      </div>

      {/* Current User Stats */}
      <motion.div
        className="w-full mb-6 p-4 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 rounded-2xl border-2 border-purple-200 dark:border-purple-700"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{currentUserData.avatar}</span>
            <div>
              <h3 className="font-semibold text-purple-700 dark:text-purple-300">{currentUserData.name}</h3>
              <p className="text-sm text-purple-600 dark:text-purple-400">Your Stats</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-700 dark:text-purple-300">{currentUserData.coins}</div>
              <div className="text-xs text-purple-600 dark:text-purple-400">Coins</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-700 dark:text-purple-300">{currentUserData.streak}</div>
              <div className="text-xs text-purple-600 dark:text-purple-400">Streak</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Leaderboard Content */}
      <div className="w-full">
        {activeTab === 'global' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">Global Leaderboard</h3>
              <p className="text-gray-600 dark:text-gray-400">Top learners from around the world</p>
            </div>
            {renderLeaderboardTable(globalLeaderboard, true)}
          </motion.div>
        )}

        {activeTab === 'friends' && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">Friends Leaderboard</h3>
              <p className="text-gray-600 dark:text-gray-400">Compete with your friends</p>
            </div>
            {renderLeaderboardTable(friendsLeaderboard, true)}
          </motion.div>
        )}
      </div>

      {/* Motivational Message */}
      <motion.div
        className="mt-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {activeTab === 'global' 
            ? "Keep learning to climb the global rankings! 🌟" 
            : "Challenge your friends and learn together! 👥"
          }
        </p>
      </motion.div>
    </div>
  );
};

export default Leaderboard; 